import { useEffect, useRef, useState } from "react";
import { Box, Button, Menu, MenuItem } from "@mui/material";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectActiveWorkspace,
  setActiveWorkspace,
  setWorkspacePermissions,
} from "redux/authSlice";
import {
  fetchMyWorkspaces,
  fetchMyWorkspacePermissions,
} from "screens/Settings/Workspaces/WorkspacesServices";
import { toTree } from "screens/Settings/Workspaces/workspaceTree";
import { onWorkspacesChanged } from "screens/Settings/Workspaces/workspaceEvents";
import { workspaceMenuPaperSx } from "screens/Settings/Workspaces/Workspaces.styles";
import { Workspace } from "types/workspace";

// Indent per tree level, so a sub-workspace reads as part of the one above it.
const LEVEL_INDENT_PX = 16;

/**
 * General active-workspace switcher for the top bar. Lists the workspaces the current user belongs
 * to (GET secure/workspace/mine). Renders nothing when the user is in no workspace, so it stays out
 * of the way for accounts that don't use workspaces.
 */
const WorkspaceSwitcher = () => {
  const dispatch = useAppDispatch();
  const activeWorkspaceId = useAppSelector(selectActiveWorkspace);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Only the latest list request may set the list, and none after unmount.
  const listSeq = useRef<number>(0);
  const mounted = useRef<boolean>(true);

  /**
   * Reloads the list after a workspace change elsewhere (rename, move, create, archive, membership)
   * or when the menu opens, so the switcher never shows old names or nesting. It only refreshes
   * what is shown: the stale-active-workspace reset below stays a first-load check, because the
   * Settings pages temporarily make the workspace they show active even when the user isn't in it.
   */
  const refreshList = () => {
    const seq = ++listSeq.current;
    fetchMyWorkspaces()
      .then((data) => {
        if (mounted.current && seq === listSeq.current) {
          setWorkspaces(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        // Keep the list already shown: a failed refresh should not empty the switcher.
      });
  };

  useEffect(() => {
    mounted.current = true;
    const unsubscribe = onWorkspacesChanged(refreshList);
    return () => {
      mounted.current = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const seq = ++listSeq.current;
    fetchMyWorkspaces()
      .then((data) => {
        if (cancelled || seq !== listSeq.current) return;
        const list = Array.isArray(data) ? data : [];
        setWorkspaces(list);
        // Auto-clear a stale active workspace: if the persisted activeWorkspaceId is no longer
        // in the user's list (removed from it / archived / deleted since last session), keep
        // sending it would put an invalid X-Workspace-Id on every scoped request. Reset to
        // Personal so the app is usable again.
        if (activeWorkspaceId && !list.some((w) => w.id === activeWorkspaceId)) {
          dispatch(setActiveWorkspace(""));
        }
      })
      .catch(() => {
        if (!cancelled && seq === listSeq.current) setWorkspaces([]);
      });
    // Effective permissions in the active workspace — used to gate scoped actions in the UI.
    // Switching workspace reloads the page, so fetching on mount also covers switches.
    fetchMyWorkspacePermissions()
      .then((perms) => {
        if (!cancelled) dispatch(setWorkspacePermissions(perms));
      })
      .catch(() => {
        // Fail CLOSED in a workspace context: leaving the default { scoped:false } state on
        // error makes hasWorkspacePermission fail open (gated actions look enabled). If an
        // active workspace is set but we couldn't confirm permissions, assume none.
        if (!cancelled && activeWorkspaceId) {
          dispatch(
            setWorkspacePermissions({ scoped: true, admin: false, permissions: [] }),
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide only when there's genuinely nothing to show: no workspaces AND no stale active id to
  // clear. If an active id is still set (mid auto-clear, or a race), keep the switcher visible so
  // the "Personal (no workspace)" option gives the user a manual escape hatch.
  if (!workspaces.length && !activeWorkspaceId) return null;

  const active = workspaces.find((w) => w.id === activeWorkspaceId);

  const pick = (id: string) => {
    if (id === (activeWorkspaceId || "")) {
      setAnchor(null);
      return;
    }
    dispatch(setActiveWorkspace(id));
    setAnchor(null);
    // Switching the active workspace changes the X-Workspace-Id header on every scoped request,
    // but pages already on screen won't re-fetch on their own. Redux state is persisted
    // synchronously (store.subscribe), so the new selection survives a reload — reload so the
    // current page comes back with data scoped to the newly selected workspace.
    window.location.reload();
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<WorkspacesOutlinedIcon fontSize="small" />}
        endIcon={<KeyboardArrowDownIcon fontSize="small" />}
        onClick={(e) => {
          setAnchor(e.currentTarget);
          refreshList();
        }}
        // Narrower on phones so the top bar keeps room for the account menu; the name ellipsizes.
        sx={{ mr: { xs: 1, sm: 2 }, textTransform: "none", maxWidth: { xs: 140, sm: 220 }, minWidth: 0 }}
        id="workspace-switcher-button"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-controls={anchor ? "workspace-switcher-menu" : undefined}
        aria-label={`Active workspace: ${
          active ? active.name : "Personal"
        }. Change workspace`}
      >
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {active ? active.name : "Personal"}
        </Box>
      </Button>
      <Menu
        id="workspace-switcher-menu"
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: workspaceMenuPaperSx }}
        MenuListProps={{
          "aria-labelledby": "workspace-switcher-button",
          "aria-label": "Select active workspace",
        }}
      >
        <MenuItem selected={!activeWorkspaceId} onClick={() => pick("")}>
          Personal (no workspace)
        </MenuItem>
        {toTree(workspaces).map(({ workspace: w, level }) => (
          <MenuItem
            key={w.id}
            selected={w.id === activeWorkspaceId}
            onClick={() => pick(w.id)}
            sx={{ pl: `${16 + level * LEVEL_INDENT_PX}px` }}
          >
            {w.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default WorkspaceSwitcher;
